// High-level write actions. Each builds a batch of ops (entity changes plus the
// activity log entries and inbox notifications they imply) and hands it to mutate().

import type {
  Activity,
  ActivityKind,
  Channel,
  ChannelRead,
  ChatSection,
  Checklist,
  Comment,
  CustomField,
  CustomFieldValue,
  Doc,
  DocPage,
  Favorite,
  Folder,
  List,
  Location,
  Message,
  Notification,
  NotificationKind,
  Priority,
  SharePermission,
  Space,
  Status,
  Tag,
  Task,
  TaskLink,
  TimeEntry,
  View,
  ViewSettings,
  ViewType,
} from '@shared/types';
import type { Op } from '@shared/sync';
import { newId, between } from '@shared/ids';
import { defaultStatuses, defaultViewSettings, isCompleteType, VIEW_TYPE_META } from '@shared/constants';
import { del, getDb, getMe, mutate, patch, put, type Tables } from './store';
import { byOrder, effectiveStatuses, listIdsIn, statusOf, subtasksOf } from './hierarchy';
import { postUpload } from './upload';

const now = () => Date.now();

// ---------------------------------------------------------------------------
// helpers

function activity(taskId: string, kind: ActivityKind, from?: unknown, to?: unknown, meta?: Record<string, unknown>): Op {
  const a: Activity = { id: newId(), taskId, actorId: getMe(), kind, from, to, meta, createdAt: now() };
  return put('activities', a);
}

function notify(
  userIds: Iterable<string>,
  taskId: string | null,
  kind: NotificationKind,
  data: Record<string, unknown> = {},
  bucket: Notification['bucket'] = 'primary',
): Op[] {
  const me = getMe();
  const ops: Op[] = [];
  for (const userId of new Set(userIds)) {
    if (!userId || userId === me) continue;
    ops.push(
      put('notifications', {
        id: newId(),
        userId,
        taskId,
        actorId: me,
        kind,
        data,
        bucket,
        readAt: null,
        clearedAt: null,
        laterAt: null,
        createdAt: now(),
      }),
    );
  }
  return ops;
}

function nextOrder(items: { orderIndex: number }[]) {
  return items.length ? Math.max(...items.map((i) => i.orderIndex)) + 1000 : 1000;
}

// ---------------------------------------------------------------------------
// tasks

export interface NewTask {
  listId: string;
  name: string;
  parentId?: string | null;
  statusId?: string;
  assigneeIds?: string[];
  dueDate?: number | null;
  dueTime?: boolean;
  startDate?: number | null;
  priority?: Priority | null;
  tagIds?: string[];
  taskTypeId?: string | null;
  description?: string;
  customFields?: Record<string, CustomFieldValue>;
  orderIndex?: number;
  timeEstimate?: number | null;
  checklists?: Checklist[];
}

export function createTask(input: NewTask): Task {
  const { task, ops } = taskCreateOps(getDb(), input);
  mutate(ops);
  return task;
}

/** Create many tasks in one write (imports). Parents must already exist or come earlier in `inputs`.
 *  `notify: false` skips the "assigned" inbox notifications (activity is still recorded). */
export function createTasks(inputs: NewTask[], opts: { notify?: boolean } = {}): Task[] {
  const db = getDb();
  const order = new Map<string, number>();
  const tasks: Task[] = [];
  const ops: Op[] = [];
  for (const input of inputs) {
    const r = taskCreateOps(db, input, { order, notify: opts.notify });
    tasks.push(r.task);
    ops.push(...r.ops);
  }
  mutate(ops);
  return tasks;
}

/** `order` carries the next orderIndex per (list, parent) across a batch, since `db` doesn't see the batch's own tasks. */
function taskCreateOps(db: Tables, input: NewTask, batch: { order?: Map<string, number>; notify?: boolean } = {}): { task: Task; ops: Op[] } {
  const me = getMe();
  const statuses = effectiveStatuses(db, input.listId);
  const list = db.lists[input.listId];
  const workspaceId = db.spaces[list?.spaceId]?.workspaceId ?? '';
  const orderKey = `${input.listId}|${input.parentId ?? ''}`;
  let orderIndex = input.orderIndex ?? batch.order?.get(orderKey);
  if (orderIndex === undefined) {
    const siblings = Object.values(db.tasks).filter((t) => t.listId === input.listId && (t.parentId ?? null) === (input.parentId ?? null));
    orderIndex = nextOrder(siblings);
  }
  batch.order?.set(orderKey, orderIndex + 1000);
  const statusId = input.statusId ?? statuses[0]?.id ?? '';
  const status = statuses.find((s) => s.id === statusId);
  const task: Task = {
    id: newId(9),
    workspaceId,
    listId: input.listId,
    parentId: input.parentId ?? null,
    name: input.name.trim() || 'Untitled task',
    description: input.description ?? '',
    statusId,
    assigneeIds: input.assigneeIds ?? [],
    watcherIds: [me],
    priority: input.priority ?? null,
    startDate: input.startDate ?? null,
    dueDate: input.dueDate ?? null,
    dueTime: input.dueTime ?? false,
    timeEstimate: input.timeEstimate ?? null,
    tagIds: input.tagIds ?? [],
    customFields: input.customFields ?? {},
    taskTypeId: input.taskTypeId ?? list?.defaultTaskTypeId ?? null,
    orderIndex,
    createdBy: me,
    closedAt: status && isCompleteType(status.type) ? now() : null,
    checklists: input.checklists ?? [],
    createdAt: now(),
    updatedAt: now(),
  };
  const ops: Op[] = [put('tasks', task), activity(task.id, 'created')];
  if (task.parentId) ops.push(activity(task.parentId, 'subtask_add', null, task.id));
  if (batch.notify !== false) ops.push(...notify(task.assigneeIds, task.id, 'assigned'));
  return { task, ops };
}

/** Update a task and record activity / notifications for meaningful changes. */
export function updateTask(id: string, changes: Partial<Task>) {
  const db = getDb();
  const prev = db.tasks[id];
  if (!prev) return;
  const ops: Op[] = [];
  const data: Partial<Task> = { ...changes };
  const watchers = new Set([...prev.watcherIds, ...prev.assigneeIds]);

  if (changes.statusId !== undefined && changes.statusId !== prev.statusId) {
    const statuses = effectiveStatuses(db, prev.listId);
    const to = statuses.find((s) => s.id === changes.statusId);
    const wasClosed = isCompleteType(statusOf(db, prev).type);
    const nowClosed = to ? isCompleteType(to.type) : false;
    if (nowClosed && !wasClosed) data.closedAt = now();
    if (!nowClosed && wasClosed) data.closedAt = null;
    ops.push(activity(id, 'status', prev.statusId, changes.statusId));
    ops.push(...notify(watchers, id, 'status', { from: prev.statusId, to: changes.statusId }, 'other'));
  }
  if (changes.name !== undefined && changes.name !== prev.name) ops.push(activity(id, 'name', prev.name, changes.name));
  if (changes.assigneeIds) {
    const added = changes.assigneeIds.filter((u) => !prev.assigneeIds.includes(u));
    const removed = prev.assigneeIds.filter((u) => !changes.assigneeIds!.includes(u));
    for (const u of added) ops.push(activity(id, 'assignee_add', null, u));
    for (const u of removed) ops.push(activity(id, 'assignee_remove', u, null));
    ops.push(...notify(added, id, 'assigned'));
    if (added.length) data.watcherIds = [...new Set([...(changes.watcherIds ?? prev.watcherIds), ...added])];
  }
  if (changes.priority !== undefined && changes.priority !== prev.priority) {
    ops.push(activity(id, 'priority', prev.priority, changes.priority));
    ops.push(...notify(watchers, id, 'priority', { from: prev.priority, to: changes.priority }, 'other'));
  }
  if (changes.dueDate !== undefined && changes.dueDate !== prev.dueDate) {
    ops.push(activity(id, 'due_date', prev.dueDate, changes.dueDate));
    ops.push(...notify(watchers, id, 'due_date', { from: prev.dueDate, to: changes.dueDate }, 'other'));
  }
  if (changes.startDate !== undefined && changes.startDate !== prev.startDate) ops.push(activity(id, 'start_date', prev.startDate, changes.startDate));
  if (changes.tagIds) {
    for (const t of changes.tagIds.filter((x) => !prev.tagIds.includes(x))) ops.push(activity(id, 'tag_add', null, t));
    for (const t of prev.tagIds.filter((x) => !changes.tagIds!.includes(x))) ops.push(activity(id, 'tag_remove', t, null));
  }
  if (changes.timeEstimate !== undefined && changes.timeEstimate !== prev.timeEstimate) ops.push(activity(id, 'time_estimate', prev.timeEstimate, changes.timeEstimate));
  if (changes.taskTypeId !== undefined && changes.taskTypeId !== prev.taskTypeId) ops.push(activity(id, 'task_type', prev.taskTypeId, changes.taskTypeId));
  if (changes.archived !== undefined && changes.archived !== prev.archived) ops.push(activity(id, 'archived', prev.archived, changes.archived));

  ops.unshift(patch('tasks', id, data));
  mutate(ops);
}

export function setCustomField(taskId: string, field: CustomField, value: CustomFieldValue) {
  const prev = getDb().tasks[taskId];
  if (!prev) return;
  const customFields = { ...prev.customFields, [field.id]: value };
  if (value === null || value === '' || (Array.isArray(value) && value.length === 0)) delete customFields[field.id];
  mutate([
    patch('tasks', taskId, { customFields }),
    activity(taskId, 'custom_field', prev.customFields[field.id] ?? null, value, { fieldId: field.id }),
  ]);
}

export function setDescription(taskId: string, html: string) {
  const prev = getDb().tasks[taskId];
  if (!prev || prev.description === html) return;
  mutate([patch('tasks', taskId, { description: html })]);
}

export function completeTask(taskId: string) {
  const db = getDb();
  const t = db.tasks[taskId];
  if (!t) return;
  const statuses = effectiveStatuses(db, t.listId);
  const current = statusOf(db, t);
  if (isCompleteType(current.type)) {
    const open = statuses.find((s) => s.type === 'open' || s.type === 'unstarted') ?? statuses[0];
    if (open) updateTask(taskId, { statusId: open.id });
  } else {
    const closed = [...statuses].reverse().find((s) => s.type === 'closed') ?? statuses.find((s) => s.type === 'done');
    if (closed) updateTask(taskId, { statusId: closed.id });
  }
}

/** The next status in workflow order (the arrow button beside the status pill). */
export function advanceStatus(taskId: string) {
  const db = getDb();
  const t = db.tasks[taskId];
  if (!t) return;
  const statuses = effectiveStatuses(db, t.listId);
  const idx = statuses.findIndex((s) => s.id === t.statusId);
  const next = statuses[idx + 1];
  if (next) updateTask(taskId, { statusId: next.id });
}

function collectSubtree(db: Tables, id: string): string[] {
  const out = [id];
  for (const c of Object.values(db.tasks)) if (c.parentId === id) out.push(...collectSubtree(db, c.id));
  return out;
}

export function deleteTask(id: string) {
  const db = getDb();
  const ids = collectSubtree(db, id);
  const idSet = new Set(ids);
  const ops: Op[] = ids.map((x) => del('tasks', x));
  for (const c of Object.values(db.comments)) if (idSet.has(c.taskId)) ops.push(del('comments', c.id));
  for (const a of Object.values(db.activities)) if (idSet.has(a.taskId)) ops.push(del('activities', a.id));
  for (const l of Object.values(db.taskLinks)) if (idSet.has(l.fromTaskId) || idSet.has(l.toTaskId)) ops.push(del('taskLinks', l.id));
  for (const n of Object.values(db.notifications)) if (n.taskId && idSet.has(n.taskId)) ops.push(del('notifications', n.id));
  for (const f of Object.values(db.favorites)) if (f.target.type === 'task' && idSet.has(f.target.id)) ops.push(del('favorites', f.id));
  mutate(ops);
}

export function archiveTask(id: string, archived = true) {
  updateTask(id, { archived });
}

/** Move a task (and its subtasks) to another list, mapping statuses by name then by type. */
export function moveTask(id: string, listId: string) {
  const db = getDb();
  const t = db.tasks[id];
  if (!t || t.listId === listId) return;
  const targetStatuses = effectiveStatuses(db, listId);
  const mapStatus = (task: Task) => {
    const cur = statusOf(db, task);
    return (
      targetStatuses.find((s) => s.name.toLowerCase() === cur.name.toLowerCase()) ??
      targetStatuses.find((s) => s.type === cur.type) ??
      targetStatuses[0]
    )?.id;
  };
  const ops: Op[] = [];
  for (const sid of collectSubtree(db, id)) {
    const task = db.tasks[sid];
    ops.push(patch('tasks', sid, { listId, statusId: mapStatus(task) ?? task.statusId, ...(sid === id ? { parentId: null } : {}) }));
  }
  ops.push(activity(id, 'moved', t.listId, listId));
  mutate(ops);
}

export function duplicateTask(id: string): Task | undefined {
  const db = getDb();
  const src = db.tasks[id];
  if (!src) return;
  const ops: Op[] = [];
  const copy = (task: Task, parentId: string | null, nameSuffix: string): string => {
    const nid = newId(9);
    ops.push(
      put('tasks', {
        ...task,
        id: nid,
        parentId,
        name: task.name + nameSuffix,
        createdAt: now(),
        updatedAt: now(),
        createdBy: getMe(),
        orderIndex: task.orderIndex + 1,
        checklists: task.checklists.map((c) => ({ ...c, id: newId(), items: c.items.map((i) => ({ ...i, id: newId() })) })),
      }),
    );
    ops.push(activity(nid, 'created', null, null, { duplicatedFrom: task.id }));
    for (const child of subtasksOf(db, task.id)) copy(child, nid, '');
    return nid;
  };
  const nid = copy(src, src.parentId, ' (copy)');
  mutate(ops);
  return getDb().tasks[nid];
}

export function reorderTask(id: string, before: Task | undefined, after: Task | undefined, changes: Partial<Task> = {}) {
  const orderIndex = between(before?.orderIndex, after?.orderIndex);
  const prev = getDb().tasks[id];
  if (!prev) return;
  if (Object.keys(changes).length) updateTask(id, { ...changes, orderIndex });
  else mutate([patch('tasks', id, { orderIndex })]);
}

export function toggleWatcher(taskId: string, userId = getMe()) {
  const t = getDb().tasks[taskId];
  if (!t) return;
  const watcherIds = t.watcherIds.includes(userId) ? t.watcherIds.filter((u) => u !== userId) : [...t.watcherIds, userId];
  mutate([patch('tasks', taskId, { watcherIds })]);
}

// checklists ---------------------------------------------------------------

export function setChecklists(taskId: string, checklists: Checklist[]) {
  mutate([patch('tasks', taskId, { checklists })]);
}

export function addChecklist(taskId: string, name = 'Checklist') {
  const t = getDb().tasks[taskId];
  if (!t) return;
  const cl: Checklist = { id: newId(), name, orderIndex: nextOrder(t.checklists), items: [] };
  setChecklists(taskId, [...t.checklists, cl]);
  return cl;
}

// links ---------------------------------------------------------------------

export function addTaskLink(fromTaskId: string, toTaskId: string, type: TaskLink['type']) {
  if (fromTaskId === toTaskId) return;
  const link: TaskLink = { id: newId(), fromTaskId, toTaskId, type, createdAt: now() };
  const db = getDb();
  const ops: Op[] = [put('taskLinks', link), activity(fromTaskId, 'dependency', null, toTaskId, { type })];
  if (type === 'blocking') {
    const blocked = db.tasks[toTaskId];
    if (blocked) ops.push(...notify(blocked.assigneeIds, toTaskId, 'blocked', { by: fromTaskId }, 'primary'));
  }
  mutate(ops);
}

export function removeTaskLink(linkId: string) {
  mutate([del('taskLinks', linkId)]);
}

// attachments ---------------------------------------------------------------

export async function uploadAttachment(taskId: string, file: File) {
  const { url, size } = await postUpload(file);
  mutate([
    put('attachments', { id: newId(), taskId, name: file.name, size, mime: file.type, url, createdBy: getMe(), createdAt: now() }),
    activity(taskId, 'attachment', null, file.name),
  ]);
}

export function deleteAttachment(id: string) {
  mutate([del('attachments', id)]);
}

// comments ------------------------------------------------------------------

export function extractMentions(html: string): string[] {
  const ids = new Set<string>();
  for (const m of html.matchAll(/data-id="([^"]+)"/g)) ids.add(m[1]);
  return [...ids];
}

function stripHtml(html: string) {
  const div = document.createElement('div');
  div.innerHTML = html;
  return (div.textContent ?? '').trim();
}

export function addComment(taskId: string, body: string, opts: { parentId?: string | null; assigneeId?: string | null } = {}) {
  const r = commentOps(getDb(), taskId, body, opts);
  if (!r) return;
  mutate(r.ops);
  return r.comment;
}

/** Add many top-level comments in one write (imports); `notify: false` skips inbox notifications. */
export function addComments(items: { taskId: string; body: string }[], opts: { notify?: boolean } = {}): Comment[] {
  const db = getDb();
  const out: Comment[] = [];
  const ops: Op[] = [];
  for (const it of items) {
    const r = commentOps(db, it.taskId, it.body, { notify: opts.notify });
    if (!r) continue;
    out.push(r.comment);
    ops.push(...r.ops);
  }
  mutate(ops);
  return out;
}

function commentOps(
  db: Tables,
  taskId: string,
  body: string,
  opts: { parentId?: string | null; assigneeId?: string | null; notify?: boolean },
): { comment: Comment; ops: Op[] } | undefined {
  const t = db.tasks[taskId];
  if (!t) return;
  const me = getMe();
  const c: Comment = {
    id: newId(),
    taskId,
    parentId: opts.parentId ?? null,
    authorId: me,
    body,
    assigneeId: opts.assigneeId ?? null,
    resolved: false,
    createdAt: now(),
  };
  const ops: Op[] = [put('comments', c)];
  if (!t.watcherIds.includes(me)) ops.push(patch('tasks', taskId, { watcherIds: [...t.watcherIds, me] }));
  if (opts.notify === false) return { comment: c, ops };
  const mentions = extractMentions(body);
  const snippet = stripHtml(body).slice(0, 240);
  ops.push(...notify(mentions, taskId, 'mention', { snippet, commentId: c.id }));
  if (opts.assigneeId) ops.push(...notify([opts.assigneeId], taskId, 'assigned_comment', { snippet, commentId: c.id }));
  if (opts.parentId) {
    const parent = db.comments[opts.parentId];
    if (parent) ops.push(...notify([parent.authorId].filter((u) => !mentions.includes(u)), taskId, 'reply', { snippet, commentId: c.id }));
  }
  const others = [...t.watcherIds, ...t.assigneeIds].filter((u) => !mentions.includes(u) && u !== opts.assigneeId);
  ops.push(...notify(others, taskId, 'comment', { snippet, commentId: c.id }));
  return { comment: c, ops };
}

export function updateComment(id: string, changes: Partial<Comment>) {
  mutate([patch('comments', id, changes)]);
}

export function deleteComment(id: string) {
  const db = getDb();
  const ops: Op[] = [del('comments', id)];
  for (const c of Object.values(db.comments)) if (c.parentId === id) ops.push(del('comments', c.id));
  mutate(ops);
}

export function toggleReaction<T extends 'comments' | 'messages'>(c: T, id: string, emoji: string) {
  const db = getDb();
  const entity = (db[c] as Record<string, Comment | Message>)[id];
  if (!entity) return;
  const me = getMe();
  const reactions = { ...(entity.reactions ?? {}) };
  const users = reactions[emoji] ?? [];
  reactions[emoji] = users.includes(me) ? users.filter((u) => u !== me) : [...users, me];
  if (reactions[emoji].length === 0) delete reactions[emoji];
  mutate([patch(c, id, { reactions } as never)]);
}

// notifications --------------------------------------------------------------

export function markNotifications(ids: string[], changes: Partial<Notification>) {
  mutate(ids.map((id) => patch('notifications', id, changes)));
}

// hierarchy -------------------------------------------------------------------

function defaultViewsOps(location: Location, types: ViewType[]): Op[] {
  return types.map((type, i) => {
    const v: View = {
      id: newId(),
      location,
      type,
      name: VIEW_TYPE_META[type].label,
      orderIndex: i * 1000,
      settings: defaultViewSettings(type),
      createdBy: getMe(),
      createdAt: now(),
    };
    return put('views', v);
  });
}

export function createSpace(workspaceId: string, input: { name: string; color: string; icon?: string | null; private?: boolean }) {
  const db = getDb();
  const siblings = Object.values(db.spaces).filter((s) => s.workspaceId === workspaceId);
  const space: Space = {
    id: newId(),
    workspaceId,
    name: input.name.trim() || 'New Space',
    color: input.color,
    icon: input.icon ?? null,
    private: input.private ?? false,
    orderIndex: nextOrder(siblings),
    statuses: defaultStatuses(() => newId(8)),
    createdAt: now(),
  };
  const list: List = {
    id: newId(),
    spaceId: space.id,
    folderId: null,
    name: 'List',
    orderIndex: 1000,
    statuses: null,
    createdAt: now(),
  };
  mutate([
    put('spaces', space),
    ...defaultViewsOps({ type: 'space', id: space.id }, ['list', 'board']),
    put('lists', list),
    ...defaultViewsOps({ type: 'list', id: list.id }, ['list', 'board', 'calendar']),
  ]);
  return { space, list };
}

export function createFolder(spaceId: string, name: string) {
  const db = getDb();
  const folder: Folder = {
    id: newId(),
    spaceId,
    name: name.trim() || 'New Folder',
    orderIndex: nextOrder(Object.values(db.folders).filter((f) => f.spaceId === spaceId)),
    statuses: null,
    createdAt: now(),
  };
  const list: List = { id: newId(), spaceId, folderId: folder.id, name: 'List', orderIndex: 1000, statuses: null, createdAt: now() };
  mutate([
    put('folders', folder),
    ...defaultViewsOps({ type: 'folder', id: folder.id }, ['list']),
    put('lists', list),
    ...defaultViewsOps({ type: 'list', id: list.id }, ['list', 'board', 'calendar']),
  ]);
  return folder;
}

export function createList(spaceId: string, folderId: string | null, name: string) {
  const db = getDb();
  const list: List = {
    id: newId(),
    spaceId,
    folderId,
    name: name.trim() || 'New List',
    orderIndex: nextOrder(Object.values(db.lists).filter((l) => l.spaceId === spaceId && (l.folderId ?? null) === folderId)),
    statuses: null,
    createdAt: now(),
  };
  mutate([put('lists', list), ...defaultViewsOps({ type: 'list', id: list.id }, ['list', 'board', 'calendar'])]);
  return list;
}

export function renameEntity(c: 'spaces' | 'folders' | 'lists' | 'views' | 'docs' | 'chatSections', id: string, name: string) {
  const key = c === 'docs' ? 'title' : 'name';
  mutate([patch(c, id, { [key]: name } as never)]);
}

export function updateSpace(id: string, changes: Partial<Space>) {
  mutate([patch('spaces', id, changes)]);
}
export function updateFolder(id: string, changes: Partial<Folder>) {
  mutate([patch('folders', id, changes)]);
}
export function updateList(id: string, changes: Partial<List>) {
  mutate([patch('lists', id, changes)]);
}

export function setStatuses(target: { type: 'space' | 'folder' | 'list'; id: string }, statuses: Status[] | null) {
  const db = getDb();
  const ops: Op[] = [];
  const c = target.type === 'space' ? 'spaces' : target.type === 'folder' ? 'folders' : 'lists';
  ops.push(patch(c, target.id, { statuses } as never));
  // remap tasks whose status vanished
  const next = statuses ?? [];
  if (next.length) {
    const affected = listIdsIn(db, target);
    for (const t of Object.values(db.tasks)) {
      if (!affected.has(t.listId)) continue;
      if (next.some((s) => s.id === t.statusId)) continue;
      const cur = statusOf(db, t);
      const mapped = next.find((s) => s.name.toLowerCase() === cur.name.toLowerCase()) ?? next.find((s) => s.type === cur.type) ?? next[0];
      ops.push(patch('tasks', t.id, { statusId: mapped.id }));
    }
  }
  mutate(ops);
}

export function deleteLocation(loc: { type: 'space' | 'folder' | 'list'; id: string }) {
  const db = getDb();
  const ops: Op[] = [];
  const lists = listIdsIn(db, loc);
  for (const t of Object.values(db.tasks)) if (lists.has(t.listId)) ops.push(del('tasks', t.id));
  for (const id of lists) ops.push(del('lists', id));
  if (loc.type === 'space') {
    ops.push(del('spaces', loc.id));
    for (const f of Object.values(db.folders)) if (f.spaceId === loc.id) ops.push(del('folders', f.id));
  }
  if (loc.type === 'folder') ops.push(del('folders', loc.id));
  const gone = new Set([loc.id, ...lists]);
  for (const v of Object.values(db.views)) if (gone.has(v.location.id)) ops.push(del('views', v.id));
  for (const f of Object.values(db.favorites)) if (gone.has(f.target.id)) ops.push(del('favorites', f.id));
  mutate(ops);
}

/** Reorder spaces/folders/lists among siblings, or move a folder/list to another container. */
export function moveHierarchyItem(
  item: { type: 'space' | 'folder' | 'list'; id: string },
  target: { spaceId?: string; folderId?: string | null; beforeId?: string | null; afterId?: string | null },
) {
  const db = getDb();
  const ops: Op[] = [];
  if (item.type === 'space') {
    const sp = db.spaces[item.id];
    if (!sp) return;
    const sibs = Object.values(db.spaces).filter((s) => s.workspaceId === sp.workspaceId && s.id !== sp.id).sort(byOrder);
    const before = target.beforeId ? db.spaces[target.beforeId] : undefined;
    const after = target.afterId ? db.spaces[target.afterId] : undefined;
    const orderIndex = before ? between(sibs[sibs.indexOf(before) - 1]?.orderIndex, before.orderIndex) : after ? between(after.orderIndex, sibs[sibs.indexOf(after) + 1]?.orderIndex) : nextOrder(sibs);
    ops.push(patch('spaces', sp.id, { orderIndex }));
  } else if (item.type === 'folder') {
    const f = db.folders[item.id];
    if (!f) return;
    const spaceId = target.spaceId ?? f.spaceId;
    const sibs = Object.values(db.folders).filter((x) => x.spaceId === spaceId && x.id !== f.id).sort(byOrder);
    const before = target.beforeId ? db.folders[target.beforeId] : undefined;
    const after = target.afterId ? db.folders[target.afterId] : undefined;
    const orderIndex = before ? between(sibs[sibs.indexOf(before) - 1]?.orderIndex, before.orderIndex) : after ? between(after.orderIndex, sibs[sibs.indexOf(after) + 1]?.orderIndex) : nextOrder(sibs);
    ops.push(patch('folders', f.id, { spaceId, orderIndex }));
    if (spaceId !== f.spaceId) {
      for (const l of Object.values(db.lists)) if (l.folderId === f.id) ops.push(...relocateListOps(db, l.id, spaceId, f.id));
    }
  } else {
    const l = db.lists[item.id];
    if (!l) return;
    const spaceId = target.spaceId ?? l.spaceId;
    const folderId = target.folderId !== undefined ? target.folderId : l.folderId;
    const sibs = Object.values(db.lists).filter((x) => x.spaceId === spaceId && (x.folderId ?? null) === (folderId ?? null) && x.id !== l.id).sort(byOrder);
    const before = target.beforeId ? db.lists[target.beforeId] : undefined;
    const after = target.afterId ? db.lists[target.afterId] : undefined;
    const orderIndex = before ? between(sibs[sibs.indexOf(before) - 1]?.orderIndex, before.orderIndex) : after ? between(after.orderIndex, sibs[sibs.indexOf(after) + 1]?.orderIndex) : nextOrder(sibs);
    if (spaceId !== l.spaceId || (folderId ?? null) !== (l.folderId ?? null)) ops.push(...relocateListOps(db, l.id, spaceId, folderId ?? null));
    ops.push(patch('lists', l.id, { spaceId, folderId: folderId ?? null, orderIndex }));
  }
  mutate(ops);
}

/** When a list changes container its inherited statuses may change: remap its tasks by name, then type. */
function relocateListOps(db: Tables, listId: string, spaceId: string, folderId: string | null): Op[] {
  const l = db.lists[listId];
  if (!l) return [];
  const ops: Op[] = [patch('lists', listId, { spaceId, folderId })];
  if (l.statuses?.length) return ops; // own statuses travel with the list
  const folder = folderId ? db.folders[folderId] : null;
  const next = (folder?.statuses?.length ? folder.statuses : db.spaces[spaceId]?.statuses) ?? [];
  if (!next.length) return ops;
  for (const t of Object.values(db.tasks)) {
    if (t.listId !== listId || next.some((s) => s.id === t.statusId)) continue;
    const cur = statusOf(db, t);
    const mapped = next.find((s) => s.name.toLowerCase() === cur.name.toLowerCase()) ?? next.find((s) => s.type === cur.type) ?? next[0];
    ops.push(patch('tasks', t.id, { statusId: mapped.id }));
  }
  return ops;
}

// views -----------------------------------------------------------------------

export function createView(location: Location, type: ViewType, name?: string, orderIndex?: number) {
  const db = getDb();
  const siblings = Object.values(db.views).filter((v) => v.location.type === location.type && v.location.id === location.id);
  const v: View = {
    id: newId(),
    location,
    type,
    name: name ?? VIEW_TYPE_META[type].label,
    orderIndex: orderIndex ?? nextOrder(siblings),
    settings: defaultViewSettings(type),
    createdBy: getMe(),
    createdAt: now(),
  };
  mutate([put('views', v)]);
  return v;
}

/** Spaces open on an Overview: add it as the first tab, once per space. Its id derives from the space so two
 *  clients adding it at the same moment write the same view, and the space flag keeps a deleted one gone. */
export function ensureSpaceOverview(spaceId: string): View | null {
  const db = getDb();
  const space = db.spaces[spaceId];
  if (!space || space.overviewAdded) return null;
  const siblings = Object.values(db.views).filter((v) => v.location.type === 'space' && v.location.id === spaceId);
  if (siblings.some((v) => v.type === 'overview')) {
    mutate([patch('spaces', spaceId, { overviewAdded: true })]);
    return null;
  }
  const v: View = {
    id: `ov_${spaceId}`,
    location: { type: 'space', id: spaceId },
    type: 'overview',
    name: VIEW_TYPE_META.overview.label,
    orderIndex: siblings.length ? Math.min(...siblings.map((s) => s.orderIndex)) - 1000 : 0,
    settings: defaultViewSettings('overview'),
    createdBy: getMe(),
    createdAt: now(),
  };
  mutate([put('views', v), patch('spaces', spaceId, { overviewAdded: true })]);
  return v;
}

export function updateViewSettings(viewId: string, changes: Partial<ViewSettings>) {
  const v = getDb().views[viewId];
  if (!v) return;
  mutate([patch('views', viewId, { settings: { ...v.settings, ...changes } })]);
}

export function deleteView(id: string) {
  mutate([del('views', id)]);
}

export function duplicateView(id: string) {
  const v = getDb().views[id];
  if (!v) return;
  const copy: View = { ...v, id: newId(), name: `${v.name} (copy)`, orderIndex: v.orderIndex + 1, createdAt: now() };
  mutate([put('views', copy)]);
  return copy;
}

// sharing with guests ---------------------------------------------------------

export type ShareTarget = { type: 'folder' | 'list' | 'task' | 'doc'; id: string };

const SHARE_COLLECTION = { folder: 'folders', list: 'lists', task: 'tasks', doc: 'docs' } as const;

/** Give a guest access to an item (and everything inside it), change it, or take it away (null). */
export function setShare(target: ShareTarget, userId: string, level: SharePermission | null) {
  const c = SHARE_COLLECTION[target.type];
  const item = getDb()[c][target.id];
  if (!item) return;
  const shares = { ...item.shares };
  if (level) shares[userId] = level;
  else delete shares[userId];
  mutate([patch(c, target.id, { shares })]);
}

// favorites -------------------------------------------------------------------

export function toggleFavorite(target: Favorite['target']) {
  const db = getDb();
  const me = getMe();
  const existing = Object.values(db.favorites).find((f) => f.userId === me && f.target.type === target.type && f.target.id === target.id);
  if (existing) mutate([del('favorites', existing.id)]);
  else
    mutate([
      put('favorites', {
        id: newId(),
        userId: me,
        target,
        orderIndex: nextOrder(Object.values(db.favorites).filter((f) => f.userId === me)),
        createdAt: now(),
      }),
    ]);
}

// tags & custom fields -----------------------------------------------------------

export function createTag(spaceId: string, name: string, color: string): Tag {
  const tag: Tag = { id: newId(), spaceId, name: name.trim().toLowerCase(), color, createdAt: now() };
  mutate([put('tags', tag)]);
  return tag;
}

export function updateTag(id: string, changes: Partial<Tag>) {
  mutate([patch('tags', id, changes)]);
}

export function deleteTag(id: string) {
  const db = getDb();
  const ops: Op[] = [del('tags', id)];
  for (const t of Object.values(db.tasks)) if (t.tagIds.includes(id)) ops.push(patch('tasks', t.id, { tagIds: t.tagIds.filter((x) => x !== id) }));
  mutate(ops);
}

export function createCustomField(field: Omit<CustomField, 'id' | 'createdAt' | 'orderIndex'>) {
  const db = getDb();
  const cf: CustomField = { ...field, id: newId(), orderIndex: nextOrder(Object.values(db.customFields)), createdAt: now() };
  mutate([put('customFields', cf)]);
  return cf;
}

export function updateCustomField(id: string, changes: Partial<CustomField>) {
  mutate([patch('customFields', id, changes)]);
}

export function deleteCustomField(id: string) {
  const db = getDb();
  const ops: Op[] = [del('customFields', id)];
  for (const t of Object.values(db.tasks)) {
    if (id in t.customFields) {
      const cf = { ...t.customFields };
      delete cf[id];
      ops.push(patch('tasks', t.id, { customFields: cf }));
    }
  }
  mutate(ops);
}

// time tracking --------------------------------------------------------------------

export function runningTimer(db: Tables, userId: string): TimeEntry | undefined {
  return Object.values(db.timeEntries).find((e) => e.userId === userId && e.end === null);
}

export function startTimer(taskId: string) {
  const db = getDb();
  const me = getMe();
  const ops: Op[] = [];
  const running = runningTimer(db, me);
  if (running) ops.push(patch('timeEntries', running.id, { end: now() }));
  ops.push(put('timeEntries', { id: newId(), taskId, userId: me, start: now(), end: null, createdAt: now() }));
  mutate(ops);
}

export function stopTimer() {
  const running = runningTimer(getDb(), getMe());
  if (running) mutate([patch('timeEntries', running.id, { end: now() })]);
}

export function addTimeEntry(taskId: string, durationMs: number, start = now() - durationMs) {
  mutate([put('timeEntries', { id: newId(), taskId, userId: getMe(), start, end: start + durationMs, createdAt: now() })]);
}

export function trackedMs(db: Tables, taskId: string): number {
  let total = 0;
  for (const e of Object.values(db.timeEntries)) if (e.taskId === taskId) total += (e.end ?? now()) - e.start;
  return total;
}

// docs ---------------------------------------------------------------------------------

export function createDoc(workspaceId: string, location: Location | null, title = 'Untitled'): { doc: Doc; page: DocPage } {
  const doc: Doc = { id: newId(), workspaceId, location, title, icon: null, createdBy: getMe(), createdAt: now(), updatedAt: now() };
  const page: DocPage = { id: newId(), docId: doc.id, parentPageId: null, title, content: '', orderIndex: 1000, createdAt: now() };
  mutate([put('docs', doc), put('docPages', page)]);
  return { doc, page };
}

export function createDocPage(docId: string, parentPageId: string | null, title = 'Untitled') {
  const db = getDb();
  const siblings = Object.values(db.docPages).filter((p) => p.docId === docId && p.parentPageId === parentPageId);
  const page: DocPage = { id: newId(), docId, parentPageId, title, content: '', orderIndex: nextOrder(siblings), createdAt: now() };
  mutate([put('docPages', page)]);
  return page;
}

export function updateDocPage(id: string, changes: Partial<DocPage>) {
  const page = getDb().docPages[id];
  if (!page) return;
  mutate([patch('docPages', id, { ...changes, updatedBy: getMe() }), patch('docs', page.docId, {})]);
}

export function updateDoc(id: string, changes: Partial<Doc>) {
  mutate([patch('docs', id, changes)]);
}

export function deleteDoc(id: string) {
  const db = getDb();
  const ops: Op[] = [del('docs', id)];
  for (const p of Object.values(db.docPages)) if (p.docId === id) ops.push(del('docPages', p.id));
  for (const f of Object.values(db.favorites)) if (f.target.type === 'doc' && f.target.id === id) ops.push(del('favorites', f.id));
  for (const v of Object.values(db.views)) if (v.docId === id) ops.push(del('views', v.id));
  mutate(ops);
}

export function deleteDocPage(id: string) {
  const db = getDb();
  const collect = (pid: string): string[] => [pid, ...Object.values(db.docPages).filter((p) => p.parentPageId === pid).flatMap((p) => collect(p.id))];
  mutate(collect(id).map((x) => del('docPages', x)));
}

// chat -------------------------------------------------------------------------------------

export function createChannel(workspaceId: string, input: { name: string; private: boolean; description?: string; memberIds?: string[] }): Channel {
  const me = getMe();
  const ch: Channel = {
    id: newId(),
    workspaceId,
    kind: 'channel',
    name: input.name.trim(),
    description: input.description ?? '',
    private: input.private,
    memberIds: [...new Set([me, ...(input.memberIds ?? [])])],
    createdBy: me,
    createdAt: now(),
  };
  mutate([put('channels', ch)]);
  return ch;
}

export function openDm(workspaceId: string, otherUserId: string): Channel {
  const db = getDb();
  const me = getMe();
  const existing = Object.values(db.channels).find(
    (c) => c.kind === 'dm' && c.workspaceId === workspaceId && c.memberIds.length === (otherUserId === me ? 1 : 2) && c.memberIds.includes(otherUserId) && c.memberIds.includes(me),
  );
  if (existing) return existing;
  const ch: Channel = {
    id: newId(),
    workspaceId,
    kind: 'dm',
    name: '',
    private: true,
    memberIds: [...new Set([me, otherUserId])],
    createdBy: me,
    createdAt: now(),
  };
  mutate([put('channels', ch)]);
  return ch;
}

export function sendMessage(channelId: string, body: string, parentId: string | null = null) {
  const db = getDb();
  const ch = db.channels[channelId];
  const me = getMe();
  const m: Message = { id: newId(), channelId, authorId: me, parentId, body, createdAt: now() };
  const ops: Op[] = [put('messages', m)];
  const mentions = extractMentions(body);
  if (mentions.length) ops.push(...notify(mentions, null, 'mention', { snippet: stripHtml(body).slice(0, 240), channelId, messageId: m.id }));
  if (ch && !ch.memberIds.includes(me) && !ch.private) ops.push(patch('channels', channelId, { memberIds: [...ch.memberIds, me] }));
  ops.push(...markChannelReadOps(channelId));
  mutate(ops);
  return m;
}

export function updateMessage(id: string, body: string) {
  mutate([patch('messages', id, { body, editedAt: now() })]);
}

export function deleteMessage(id: string) {
  const ops: Op[] = [del('messages', id)];
  for (const m of Object.values(getDb().messages)) if (m.parentId === id) ops.push(del('messages', m.id));
  mutate(ops);
}

function markChannelReadOps(channelId: string): Op[] {
  const db = getDb();
  const me = getMe();
  const existing = Object.values(db.channelReads).find((r) => r.userId === me && r.channelId === channelId);
  if (existing) return [patch('channelReads', existing.id, { lastReadAt: now() })];
  return [put('channelReads', { id: newId(), userId: me, channelId, lastReadAt: now(), createdAt: now() })];
}

export function markChannelRead(channelId: string) {
  mutate(markChannelReadOps(channelId));
}

// chat notification settings -----------------------------------------------------

/** `mutedUntil` for "until I change it" (the latest time a Date can hold). */
export const MUTE_FOREVER = 8_640_000_000_000_000;

export type ChannelNotify = NonNullable<ChannelRead['notify']>;

/**
 * My notification settings in a conversation. Channels and DMs tell you about every new message
 * unless you switch them to mentions and threads you follow. `mutedUntil` is set only while the
 * mute lasts.
 */
export function chatPrefsOf(db: Tables, me: string, channel: Channel): { notify: ChannelNotify; mutedUntil: number | null } {
  const rec = Object.values(db.channelReads).find((r) => r.userId === me && r.channelId === channel.id);
  const until = rec?.mutedUntil ?? null;
  return { notify: rec?.notify ?? 'all', mutedUntil: until && until > Date.now() ? until : null };
}

export function setChatPrefs(channelId: string, changes: { notify?: ChannelNotify; mutedUntil?: number | null; sectionId?: string | null }) {
  const db = getDb();
  const me = getMe();
  const existing = Object.values(db.channelReads).find((r) => r.userId === me && r.channelId === channelId);
  if (existing) mutate([patch('channelReads', existing.id, changes)]);
  else mutate([put('channelReads', { id: newId(), userId: me, channelId, lastReadAt: now(), createdAt: now(), ...changes })]);
}

// chat sidebar sections (personal groups of conversations) -----------------------

export function chatSectionsOf(db: Tables, me: string, workspaceId: string): ChatSection[] {
  return Object.values(db.chatSections)
    .filter((s) => s.userId === me && s.workspaceId === workspaceId)
    .sort((a, b) => a.orderIndex - b.orderIndex);
}

/** My section for each conversation I've filed (sections that no longer exist are ignored). */
export function sectionByChannel(db: Tables, me: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const r of Object.values(db.channelReads)) if (r.userId === me && r.sectionId && db.chatSections[r.sectionId]) out[r.channelId] = r.sectionId;
  return out;
}

/** A new section at the bottom of my list, optionally with a conversation moved into it. */
export function createChatSection(workspaceId: string, name: string, channelId?: string): ChatSection {
  const db = getDb();
  const me = getMe();
  const last = chatSectionsOf(db, me, workspaceId).at(-1);
  const section: ChatSection = { id: newId(), userId: me, workspaceId, name: name.trim(), orderIndex: (last?.orderIndex ?? 0) + 1000, createdAt: now() };
  mutate([put('chatSections', section)]);
  if (channelId) setChatPrefs(channelId, { sectionId: section.id });
  return section;
}

/** Move a conversation into one of my sections, or back out (null). */
export function moveToSection(channelId: string, sectionId: string | null) {
  setChatPrefs(channelId, { sectionId });
}

/** Delete a section; its conversations go back to Channels / Direct Messages. */
export function deleteChatSection(id: string) {
  const db = getDb();
  const me = getMe();
  const ops: Op[] = [del('chatSections', id)];
  for (const r of Object.values(db.channelReads)) if (r.userId === me && r.sectionId === id) ops.push(patch('channelReads', r.id, { sectionId: null }));
  mutate(ops);
}

/** Swap a section with its neighbour above (-1) or below (1). */
export function moveChatSection(id: string, dir: -1 | 1) {
  const db = getDb();
  const section = db.chatSections[id];
  if (!section) return;
  const list = chatSectionsOf(db, getMe(), section.workspaceId);
  const i = list.findIndex((s) => s.id === id);
  const other = list[i + dir];
  if (!other) return;
  mutate([patch('chatSections', id, { orderIndex: other.orderIndex }), patch('chatSections', other.id, { orderIndex: section.orderIndex })]);
}

/** I follow a thread when I started it, replied in it, or was mentioned anywhere in it. */
export function followsThread(db: Tables, parentId: string, me: string): boolean {
  const parent = db.messages[parentId];
  if (parent && (parent.authorId === me || extractMentions(parent.body).includes(me))) return true;
  return Object.values(db.messages).some((m) => m.parentId === parentId && (m.authorId === me || extractMentions(m.body).includes(me)));
}

// misc -----------------------------------------------------------------------------------

export function sortedByOrder<T extends { orderIndex: number }>(items: T[]) {
  return [...items].sort(byOrder);
}
